import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus, X, Save, CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import { OBRAS, fmt } from './insumos.utils';

function InventarioDetalhe({ inventarioId, onClose }) {
  const qc = useQueryClient();
  const [salvando, setSalvando] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const [erro, setErro] = useState('');

  const { data, refetch } = useQuery({
    queryKey: ['inventario-detalhe', inventarioId],
    queryFn: () => api.get(`/insumos/inventarios/${inventarioId}`).then(r => r.data.data),
  });

  async function salvarItem(itemId, saldo_contado, justificativa) {
    setSalvando(itemId);
    try {
      await api.put(`/insumos/inventarios/${inventarioId}/itens/${itemId}`, { saldo_contado, justificativa });
      refetch();
    } finally { setSalvando(null); }
  }

  async function finalizar() {
    if (!window.confirm('Finalizar inventário? Isso irá ajustar os saldos do estoque.')) return;
    setFinalizando(true);
    try {
      await api.post(`/insumos/inventarios/${inventarioId}/finalizar`);
      qc.invalidateQueries(['insumos-inventarios']);
      qc.invalidateQueries(['insumos-saldo']);
      onClose();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao finalizar');
    } finally { setFinalizando(false); }
  }

  if (!data) return <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"><Loader2 className="animate-spin text-white" size={32} /></div>;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-semibold text-gray-800">Inventário — {data.obra}</h3>
            <p className="text-xs text-gray-400">{format(parseISO(data.data_inventario), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</p>
          </div>
          <div className="flex items-center gap-2">
            {data.status === 'rascunho' && (
              <button onClick={finalizar} disabled={finalizando}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                {finalizando ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Finalizar Inventário
              </button>
            )}
            <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
          </div>
        </div>

        {erro && <div className="m-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex gap-2"><AlertTriangle size={16} />{erro}</div>}

        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 sticky top-0 border-b">
              <tr>
                <th className="px-4 py-2 text-left">Material</th>
                <th className="px-4 py-2 text-right">Saldo Sistema</th>
                <th className="px-4 py-2 text-right w-36">Saldo Contado</th>
                <th className="px-4 py-2 text-right">Diferença</th>
                <th className="px-4 py-2 text-left min-w-40">Justificativa</th>
                <th className="px-4 py-2 text-center w-10"></th>
              </tr>
            </thead>
            <tbody>
              {(data.itens || []).map(item => {
                const dif = parseFloat(item.saldo_contado || 0) - parseFloat(item.saldo_sistema || 0);
                return (
                  <InventarioItemRow
                    key={item.id}
                    item={{ ...item, diferenca_calc: dif }}
                    readonly={data.status === 'finalizado'}
                    salvando={salvando === item.id}
                    onSave={salvarItem}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {data.status === 'finalizado' && (
          <div className="p-4 bg-green-50 border-t text-sm text-green-700 flex items-center gap-2">
            <CheckCircle size={16} /> Inventário finalizado — saldos ajustados.
          </div>
        )}
      </div>
    </div>
  );
}

function InventarioItemRow({ item, readonly, salvando, onSave }) {
  const [contado, setContado] = useState(item.saldo_contado ?? item.saldo_sistema);
  const [just, setJust] = useState(item.justificativa || '');
  const dif = parseFloat(contado || 0) - parseFloat(item.saldo_sistema || 0);
  const changed = parseFloat(contado) !== parseFloat(item.saldo_contado);

  return (
    <tr className={`border-b border-gray-50 ${Math.abs(dif) > 0 ? 'bg-orange-50' : ''}`}>
      <td className="px-4 py-2">
        <p className="font-medium text-gray-800 text-sm">{item.material_nome}</p>
        <p className="text-xs text-gray-400">{item.codigo} · {item.unidade_sigla}</p>
      </td>
      <td className="px-4 py-2 text-right font-mono text-gray-600">{fmt(item.saldo_sistema)}</td>
      <td className="px-4 py-2">
        <input type="number" min="0" step="0.01" value={contado} onChange={e => setContado(e.target.value)} disabled={readonly}
          className={`w-full text-right border rounded px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-400 ${readonly ? 'bg-gray-50' : ''}`} />
      </td>
      <td className="px-4 py-2 text-right font-mono font-medium">
        <span className={dif > 0 ? 'text-green-600' : dif < 0 ? 'text-red-600' : 'text-gray-400'}>
          {dif > 0 ? '+' : ''}{fmt(dif)}
        </span>
      </td>
      <td className="px-4 py-2">
        <input value={just} onChange={e => setJust(e.target.value)} disabled={readonly || Math.abs(dif) === 0}
          placeholder={Math.abs(dif) > 0 ? 'Informe o motivo...' : ''}
          className={`w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 ${readonly ? 'bg-gray-50' : ''}`} />
      </td>
      <td className="px-4 py-2 text-center">
        {!readonly && changed && (
          <button onClick={() => onSave(item.id, contado, just)} disabled={salvando}
            className="p-1 rounded hover:bg-blue-50 text-blue-500">
            {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          </button>
        )}
      </td>
    </tr>
  );
}

export default function InsumosInventario() {
  const [obraModal, setObraModal] = useState('');
  const [criandoInv, setCriandoInv] = useState(false);
  const [detalheId, setDetalheId] = useState(null);
  const [obraFiltro, setObraFiltro] = useState('');
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['insumos-inventarios', obraFiltro],
    queryFn: () => api.get('/insumos/inventarios', { params: { obra: obraFiltro || undefined } }).then(r => r.data.data),
  });

  const inventarios = data || [];

  async function criarInventario() {
    if (!obraModal) return;
    setCriandoInv(true);
    try {
      const { data } = await api.post('/insumos/inventarios', { obra: obraModal });
      qc.invalidateQueries(['insumos-inventarios']);
      setDetalheId(data.data.id);
      setObraModal('');
    } finally { setCriandoInv(false); }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList size={22} className="text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-800">Inventários</h1>
            <p className="text-sm text-gray-500">Balanço periódico e ajuste de estoque</p>
          </div>
        </div>
      </div>

      {/* Criar novo */}
      <div className="bg-white rounded-xl border p-4 flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Criar novo inventário para obra</label>
          <select value={obraModal} onChange={e => setObraModal(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="">Selecionar obra...</option>
            {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <button onClick={criarInventario} disabled={criandoInv || !obraModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">
          {criandoInv ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Iniciar Inventário
        </button>
      </div>

      <select value={obraFiltro} onChange={e => setObraFiltro(e.target.value)}
        className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
        <option value="">Todas as obras</option>
        {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>

      {/* Lista */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 border-b">
            <tr>
              <th className="px-4 py-3 text-left">Obra</th>
              <th className="px-4 py-3 text-left">Data</th>
              <th className="px-4 py-3 text-left">Responsável</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {inventarios.length ? inventarios.map(inv => (
              <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{inv.obra}</td>
                <td className="px-4 py-3 text-gray-600">{format(parseISO(inv.data_inventario), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</td>
                <td className="px-4 py-3 text-gray-500">{inv.responsavel || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${inv.status === 'finalizado' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {inv.status === 'finalizado' ? 'Finalizado' : 'Em andamento'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => setDetalheId(inv.id)} className="text-xs text-blue-600 hover:underline">
                    {inv.status === 'rascunho' ? 'Continuar' : 'Ver'}
                  </button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={5} className="text-center py-12 text-gray-400">Nenhum inventário criado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detalheId && <InventarioDetalhe inventarioId={detalheId} onClose={() => setDetalheId(null)} />}
    </div>
  );
}
